import { TransactionScheduleFieldsFragment } from '../../graphql/generated';
import { getRecurrenceConfig } from '../../types';
import { dt } from '../../util/dateTime';
import { capitalize } from '../../util/strings';
import { getGoalDirectionWords } from './getGoalDirectionWords';
import { GoalDirection, TransactionProps } from './index';

export const getTransactionSummaryProps = ({
    transaction,
    transactionSchedule,
    goalDirection,
}: {
    transaction: TransactionProps;
    goalDirection: GoalDirection;
    transactionSchedule?: TransactionScheduleFieldsFragment;
}): { subtitle: string; title: string } => {
    const directionWords = getGoalDirectionWords(goalDirection);

    const txnDateStr = dt(transaction.plannedProcessingDate).format(
        'MMM D, YYYY'
    );

    let statusStr = '';
    let subtitle = '';

    switch (transaction.status) {
        case undefined:
            break;

        case 'COMPLETED':
            statusStr = 'completed';
            break;

        case 'SCHEDULED':
            statusStr = 'scheduled';
            subtitle = capitalize(directionWords.gerund) + ' on ' + txnDateStr;
            break;

        case 'PLANNED':
        case 'QUEUED':
        case 'REQUESTING':
        case 'EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED':
        case 'EXTERNAL_ACCOUNT_DISCONNECTED':
        case 'EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE':
        case 'EXTERNAL_ACCOUNT_MISSING':
        case 'REQUESTED':
        case 'DELAYED':
        case 'PROCESSING':
        case 'TRADE_INITIATED':
        case 'TRADE_SETTLED':
        case 'TRANSFER_PLANNED':
        case 'TRANSFER_QUEUED':
        case 'TRANSFER_REQUESTING':
        case 'TRANSFER_REQUESTED':
        case 'COMMITTED': // TODO: verify what this represents
            if (dt(transaction.plannedProcessingDate).isAfter(dt(), 'day')) {
                statusStr = 'scheduled';
                subtitle =
                    capitalize(directionWords.gerund) + ' on ' + txnDateStr;
            } else {
                statusStr = 'in progress';
                subtitle =
                    capitalize(directionWords.noun) + ' began on ' + txnDateStr;
            }
            break;

        case 'CANCELED': {
            const isNSF =
                transaction.cancellationReason === 'INSUFFICIENT_FUNDS';

            if (isNSF) {
                statusStr = 'failed';
                subtitle =
                    'There were insufficient funds to complete the request on ' +
                    txnDateStr;
            } else {
                statusStr = 'canceled';
            }

            break;
        }

        case 'EXPIRED':
        case 'FAILED':
            statusStr = 'failed';
            break;

        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = transaction.status;
        }
    }

    let freqStr = 'one time';
    if (transactionSchedule) {
        freqStr = getRecurrenceConfig(
            transactionSchedule.recurrence
        ).adverbPhrase;
        statusStr = '';
        subtitle = 'Next deposit ' + txnDateStr;
    } else if (transaction.transactionScheduleId) {
        freqStr = 'recurring';
    }

    return {
        title: `${capitalize(freqStr)} ${directionWords.noun} ${statusStr}`,
        subtitle,
    };
};
